// src/context/PairingContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { mentoringApi } from '../axiosInstance';

const PairingContext = createContext(null);

const STORAGE_KEY = 'menvio.selectedPairingId';

/**
 * Helper: aktuellen matching_id-Parameter aus HashRouter-URL lesen.
 * Beispiel: /#/mentor-dashboard?pairing=74  ->  74
 */
function readPairingIdFromUrl() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart === -1) return null;
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  const raw = params.get('pairing');
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Helper: matching_id in URL zurueckschreiben, ohne Navigation auszuloesen.
 */
function writePairingIdToUrl(matchingId) {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '#/';
  const [path, queryStr] = hash.includes('?')
    ? hash.split('?')
    : [hash, ''];
  const params = new URLSearchParams(queryStr);
  if (matchingId) {
    params.set('pairing', String(matchingId));
  } else {
    params.delete('pairing');
  }
  const queryString = params.toString();
  const newHash = queryString ? `${path}?${queryString}` : path;
  if (newHash !== window.location.hash) {
    window.history.replaceState(null, '', newHash);
  }
}

/**
 * Liest die initial gewuenschte matching_id.
 * Reihenfolge: URL > localStorage > null (Auto-Select im Effect).
 */
function readInitialPairingId() {
  const fromUrl = readPairingIdFromUrl();
  if (fromUrl) return fromUrl;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

const ACTIVE_STATUSES = ['active', 'bestätigt', 'confirmed'];

export function PairingProvider({ user, children }) {
  const [selectedId, setSelectedIdState] = useState(() => readInitialPairingId());

  const { data: pairings = [], isLoading, error } = useQuery({
    queryKey: ['my-pairings', user?.id],
    queryFn: async () => {
      try {
        const res = await mentoringApi.get('/my-pairings');
        return Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          return [];
        }
        throw err;
      }
    },
    enabled: !!user?.id && ['mentor', 'mentee'].includes(String(user?.role || user?.mentoring_role || '').toLowerCase()),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Auto-Select: wenn nichts ausgewaehlt ist, nimm das erste aktive Pairing.
  useEffect(() => {
    if (selectedId || !pairings.length) return;
    const firstActive = pairings.find((p) =>
      ACTIVE_STATUSES.includes(p.status)
    );
    if (firstActive?.matching_id) {
      setSelectedIdState(firstActive.matching_id);
    }
  }, [selectedId, pairings]);

  // Reset: falls selectedId nicht mehr in der Liste existiert (Pairing beendet o.ae.),
  // auf null zuruecksetzen, damit Auto-Select erneut greift.
  useEffect(() => {
    if (!selectedId || !pairings.length) return;
    const exists = pairings.some((p) => p.matching_id === selectedId);
    if (!exists) {
      setSelectedIdState(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [selectedId, pairings]);

  // Spiegel: selectedId in URL und localStorage persistieren.
  useEffect(() => {
    writePairingIdToUrl(selectedId);
    try {
      if (selectedId) {
        localStorage.setItem(STORAGE_KEY, String(selectedId));
      }
    } catch {
      /* ignore */
    }
  }, [selectedId]);

  const setSelectedPairing = useCallback((matchingId) => {
    const parsed = Number(matchingId);
    setSelectedIdState(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }, []);

  const selectedPairing = useMemo(
    () => pairings.find((p) => p.matching_id === selectedId) || null,
    [pairings, selectedId]
  );

  const value = useMemo(
    () => ({
      pairings,
      selectedPairing,
      selectedMatchingId: selectedId,
      setSelectedPairing,
      loading: isLoading,
      error,
    }),
    [pairings, selectedPairing, selectedId, setSelectedPairing, isLoading, error]
  );

  return (
    <PairingContext.Provider value={value}>
      {children}
    </PairingContext.Provider>
  );
}

/**
 * Hook fuer Konsumenten.
 * Fallback (leerer Default) falls kein Provider im Tree ist, damit Komponenten
 * nicht brechen (z.B. auf Public-Pages, wenn PairingProvider bewusst aussen bleibt).
 */
export function usePairing() {
  const ctx = useContext(PairingContext);
  if (!ctx) {
    return {
      pairings: [],
      selectedPairing: null,
      selectedMatchingId: null,
      setSelectedPairing: () => {},
      loading: false,
      error: null,
    };
  }
  return ctx;
}

export default PairingContext;
